import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { PriceChangeIndicator } from '@/components/PriceChangeIndicator'
import { Label } from '@/components/Label'
import { Skeleton } from '@/components/Skeleton'
import { LottieView } from '@/components/LottieView'
import { useTheme } from '@/hooks/useTheme'
import { useFetchCryptoPriceHistory, Blockchains, Periods } from '@/domain'
import { periods } from '@/constants/periods'
import { lineChartAnimation } from '@/assets/json'
import { Bubble } from './subComponents/Bubble'
import { Cursor } from './subComponents/Cursor'
import { VerticalDottedLine } from './subComponents/VerticalDottedLine'

interface AnimatedLineChartProps {
  code?: string
  cryptoName?: string
  referenceColor?: string
  scrollViewRef?: React.RefObject<HTMLDivElement>
  pricePerUnit?: number
  percentageChange24h?: number
}

// Periods are now imported from constants

// Mock data for demonstration (matching API structure)
const generateMockData = (period: string) => {
  const points =
    period === 'day' ? 24 : period === 'week' ? 7 : period === 'month' ? 30 : 12
  const basePrice = 45000 + Math.random() * 10000

  return Array.from({ length: points }, (_, i) => ({
    time: Date.now() - (points - i) * 3600000,
    priceUsd: basePrice + (Math.random() - 0.5) * 5000,
    date: new Date(Date.now() - (points - i) * 3600000).toISOString(),
    lineChartDateFormatted: new Date(
      Date.now() - (points - i) * 3600000,
    ).toLocaleDateString(),
  }))
}

export const AnimatedLineChart: React.FC<AnimatedLineChartProps> = ({
  code = 'BTC',
  cryptoName = 'Bitcoin',
  referenceColor = '#F7931A',
  pricePerUnit = 0,
  percentageChange24h = 0,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [selectedRange, setSelectedRange] = useState(periods[0]) // Default to day
  const [showCursor, setShowCursor] = useState(false)
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 })
  const [selectedValue, setSelectedValue] = useState(0)
  const [selectedDateFormatted, setSelectedDateFormatted] = useState('')
  const [isPressed, setIsPressed] = useState(false)
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Fetch real crypto price history data
  const { data: cryptoHistory, isLoading: isLoadingChartData } =
    useFetchCryptoPriceHistory({
      blockchain: code?.toLowerCase() as Blockchains,
      period: selectedRange.period as Periods,
    })

  const blockchainHistory = cryptoHistory?.data
  const firstPrice = blockchainHistory?.[0]?.priceUsd || 0
  const lastPrice =
    blockchainHistory?.[blockchainHistory?.length - 1]?.priceUsd || 0

  // Calculate percentage change from historical data if available, otherwise use prop
  const calculatedPercentageChange = blockchainHistory?.length
    ? ((lastPrice - firstPrice) / firstPrice) * 100
    : percentageChange24h || 0

  // Use real price data - prefer historical data if available, otherwise use props
  const currentPrice = lastPrice || pricePerUnit || 0
  const realPercentageChange =
    calculatedPercentageChange || percentageChange24h || 0

  // Format the price with proper thousands separators (matching RN format)
  const formattedValue = `$${(Math.floor(currentPrice * 100) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`

  // Simple SVG line chart - make it responsive and match RN dimensions
  const CHART_HEIGHT = 124.53
  const chartHeight = CHART_HEIGHT
  const padding = 20

  // Skeleton dimensions matching RN version
  const skeletonWidth = 300
  const skeletonHeight = 54 + 20

  // Get chart width from container or use window width
  const [chartWidth, setChartWidth] = useState(0)
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setChartWidth(containerRef.current.offsetWidth || window.innerWidth)
      } else {
        setChartWidth(window.innerWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // Use real chart data if available, otherwise fallback to mock data
  const chartData = blockchainHistory || generateMockData(selectedRange.period)

  const minPrice = Math.min(...chartData.map((d) => d.priceUsd))
  const maxPrice = Math.max(...chartData.map((d) => d.priceUsd))
  const priceRange = maxPrice - minPrice || 1

  // Calculate path data using smooth curves (like React Native)
  const viewBoxWidth = 5750

  // Generate smooth curve using cubic bezier curves
  const generateSmoothPath = (points: Array<{ x: number; y: number }>) => {
    if (points.length < 2) return ''

    let path = `M ${points[0].x} ${points[0].y}`

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1]
      const curr = points[i]
      const next = points[i + 1]

      if (i === 1) {
        // First curve - use current and next point for control
        const cp1x = prev.x + (curr.x - prev.x) * 0.3
        const cp1y = prev.y
        const cp2x =
          curr.x - (next ? (next.x - prev.x) * 0.2 : (curr.x - prev.x) * 0.3)
        const cp2y = curr.y
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`
      } else if (i === points.length - 1) {
        // Last curve - use previous and current point for control
        const prevPrev = points[i - 2]
        const cp1x = prev.x + (curr.x - prevPrev.x) * 0.2
        const cp1y = prev.y
        const cp2x = curr.x - (curr.x - prev.x) * 0.3
        const cp2y = curr.y
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`
      } else {
        // Middle curves - use surrounding points for smooth control
        const prevPrev = points[i - 2] || prev
        const cp1x = prev.x + (curr.x - prevPrev.x) * 0.2
        const cp1y = prev.y
        const cp2x = curr.x - (next.x - prev.x) * 0.2
        const cp2y = curr.y
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`
      }
    }

    return path
  }

  // Convert chart data to points
  const points = chartData.map((point, index) => {
    // eslint-disable-next-line max-len
    const x =
      padding + (index / (chartData.length - 1)) * (viewBoxWidth - 2 * padding)
    // eslint-disable-next-line max-len
    const y =
      padding +
      (1 - (point.priceUsd - minPrice) / priceRange) *
        (chartHeight - 2 * padding)
    return { x, y }
  })

  const pathData = generateSmoothPath(points)

  // Update cursor position based on clientX
  const updateCursorPosition = (clientX: number) => {
    if (!svgRef.current || !containerRef.current) return

    const svgRect = svgRef.current.getBoundingClientRect()

    // Get mouse position relative to SVG
    let mouseX = clientX - svgRect.left

    // Clamp mouseX to SVG bounds
    mouseX = Math.max(0, Math.min(mouseX, svgRect.width))

    // Convert to viewBox coordinates
    const viewBoxX = (mouseX / svgRect.width) * viewBoxWidth

    // Find the closest data point
    const stepX = (viewBoxWidth - 2 * padding) / (chartData.length - 1)
    const index = Math.round((viewBoxX - padding) / stepX)
    const clampedIndex = Math.max(0, Math.min(index, chartData.length - 1))

    if (chartData[clampedIndex]) {
      const dataPoint = chartData[clampedIndex]

      // Calculate the actual point position
      const pointX = points[clampedIndex].x
      const pointY = points[clampedIndex].y

      // Convert viewBox coordinates back to screen coordinates
      const screenX = (pointX / viewBoxWidth) * svgRect.width
      const screenY = (pointY / chartHeight) * svgRect.height

      setCursorPosition({ x: screenX, y: screenY })
      setSelectedValue(dataPoint.priceUsd)
      setSelectedDateFormatted(dataPoint.lineChartDateFormatted)
    }
  }

  // Handle mouse/touch down
  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    setIsPressed(true)
    setShowCursor(true)

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    updateCursorPosition(clientX)
  }

  // Handle hover (only for mouse, not touch)
  const handleMouseEnter = () => {
    if (!isPressed) {
      setShowCursor(true)
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPressed) {
      updateCursorPosition(e.clientX)
    }
  }

  const handleMouseLeave = () => {
    if (!isPressed) {
      setShowCursor(false)
    }
  }

  // Add global event listeners for touch/mouse move and up when pressed
  useEffect(() => {
    if (!isPressed) return

    const handleGlobalMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault()
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      updateCursorPosition(clientX)
    }

    const handleGlobalUp = () => {
      setIsPressed(false)
      setShowCursor(false)
    }

    // Add listeners to document to track movement outside SVG
    document.addEventListener('mousemove', handleGlobalMove)
    document.addEventListener('mouseup', handleGlobalUp)
    document.addEventListener('touchmove', handleGlobalMove, { passive: false })
    document.addEventListener('touchend', handleGlobalUp)
    document.addEventListener('touchcancel', handleGlobalUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMove)
      document.removeEventListener('mouseup', handleGlobalUp)
      document.removeEventListener('touchmove', handleGlobalMove)
      document.removeEventListener('touchend', handleGlobalUp)
      document.removeEventListener('touchcancel', handleGlobalUp)
    }
  }, [isPressed, chartData, points, viewBoxWidth, chartHeight, padding])

  return (
    <BaseView data-component="animated-line-chart" center gap={28}>
      {/* Price Display - Skeleton when loading */}
      {isLoadingChartData && (
        <Skeleton
          viewBox={`0 0 ${skeletonWidth} ${skeletonHeight}`}
          style={{
            width: skeletonWidth,
            height: skeletonHeight,
            alignSelf: 'center',
          }}
        >
          <rect rx="8" ry="8" width={skeletonWidth} height={skeletonHeight} />
        </Skeleton>
      )}

      {/* Price Display - Actual price when loaded */}
      {!isLoadingChartData && (
        <BaseText
          fontSize={56}
          fontFamily="bold"
          color="label.default"
          textAlign="center"
          style={{
            lineHeight: '64px',
            letterSpacing: '-0.5px',
            transition: 'opacity 0.2s ease-in-out',
            minHeight: `${skeletonHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {formattedValue}
        </BaseText>
      )}

      {/* Price Info */}
      <BaseView row justify="space-around" align="center" fullWidth px={36}>
        <Label
          type="descriptionBottomSheet"
          skeletonActive={isLoadingChartData}
          skeletonWidth={77}
        >
          {cryptoName} {t('translation:price')}
        </Label>
        <PriceChangeIndicator
          percentageChange24h={realPercentageChange}
          revenue24hFiatAmount={0}
          showRevenue={false}
          skeletonActive={isLoadingChartData}
        />
      </BaseView>

      {/* Chart - Lottie animation when loading */}
      {isLoadingChartData && (
        <LottieView
          autoPlay
          loop
          source={lineChartAnimation}
          style={{
            width: chartWidth || '100%',
            height: CHART_HEIGHT,
          }}
        />
      )}

      {/* Chart - Actual chart when loaded */}
      {!isLoadingChartData && blockchainHistory && (
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            width: '100%',
          }}
        >
          <svg
            ref={svgRef}
            width="100%"
            height={chartHeight}
            viewBox={`0 0 ${viewBoxWidth} ${chartHeight}`}
            preserveAspectRatio="none"
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              borderRadius: '12px',
              cursor: 'crosshair',
              touchAction: 'none',
              // backgroundColor: theme.colors.background.cardItemRoot,
            }}
          >
            {/* Grid lines */}
            {/* <defs>
              <pattern
                id="grid"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 20 0 L 0 0 0 20"
                  fill="none"
                  stroke={theme.colors.background.secondaryGray + '40'}
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" /> */}

            {/* Price line */}
            <path
              d={pathData}
              fill="none"
              stroke={referenceColor}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{
                filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1))',
              }}
            />

            {/* Gradient fill */}
            <defs>
              <linearGradient
                id="priceGradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stopColor={referenceColor}
                  stopOpacity="0.3"
                />
                <stop
                  offset="100%"
                  stopColor={referenceColor}
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>
            <path
              d={`${pathData} L ${points[points.length - 1]?.x || viewBoxWidth - padding} ${chartHeight - padding} L ${points[0]?.x || padding} ${chartHeight - padding} Z`}
              fill="url(#priceGradient)"
            />

            {/* Vertical dotted line when cursor is shown */}
            {showCursor && (
              <VerticalDottedLine
                cx={
                  (cursorPosition.x /
                    (svgRef.current?.getBoundingClientRect().width || 1)) *
                  viewBoxWidth
                }
                cy={(cursorPosition.y / chartHeight) * chartHeight}
                chartHeight={chartHeight}
                color={referenceColor}
              />
            )}
          </svg>

          {/* Cursor and Bubble positioned absolutely */}
          {showCursor && containerRef.current && (
            <>
              <Cursor
                cx={cursorPosition.x}
                cy={cursorPosition.y}
                color={referenceColor}
              />
              <Bubble
                cx={cursorPosition.x}
                cy={cursorPosition.y}
                selectedValue={selectedValue}
                selectedDateFormatted={selectedDateFormatted}
                containerWidth={containerRef.current.offsetWidth}
              />
            </>
          )}
        </div>
      )}

      {/* Period Buttons */}
      <BaseView row center gap={8} px={0} fullWidth>
        {periods.map((period) => {
          const isSelected = period.period === selectedRange.period

          return (
            <AppButton
              key={period.id}
              onClick={() => setSelectedRange(period)}
              label={
                period.period === 'day'
                  ? 'Day'
                  : period.period === 'week'
                    ? 'Week'
                    : period.period === 'month'
                      ? 'Month'
                      : period.period === 'year'
                        ? 'Year'
                        : period.period.toUpperCase()
              }
              size="small"
              type="link"
              isSharedSpace
              labelProps={{
                style: {
                  color: isSelected
                    ? theme.colors.label.default
                    : theme.colors.label.disabled,
                },
              }}
              style={{
                fontSize: '18px',
                paddingTop: '8px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                paddingRight: '12px',
                borderRadius: '50px',
                border: 'none',
                backgroundColor: isSelected
                  ? theme.colors.background.secondaryGrayOpacity01
                  : 'transparent',
                background: isSelected
                  ? theme.colors.background.secondaryGrayOpacity01
                  : 'transparent',
              }}
            />
          )
        })}
      </BaseView>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </BaseView>
  )
}
